import React, { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Button,
  Box,
  Container,
  Drawer,
  IconButton,
  useTheme,
  useMediaQuery,
  TextField,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useApi } from "../../utils/api";
import AddClient from "./AddClient";
import Loading from "../Layout/Loading";
import ClientWorkoutCalendar from "./Client";
import ManageProductsDialog from "./ManageProductsDialog";

const ClientsList = ({
  clients,
  activeClient,
  setActiveClient,
  setOpenAddClient,
  isMobile,
  setDrawerOpen,
  theme,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = React.useRef();

  const filteredClients = React.useMemo(
    () =>
      clients.filter(
        (client) =>
          client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          client.email.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [clients, searchQuery]
  );

  React.useEffect(() => {
    if (filteredClients.length > 0) {
      if (
        !activeClient ||
        !filteredClients.find((c) => c.id === activeClient.id)
      ) {
        setActiveClient(filteredClients[0]);
      }
    } else if (clients.length > 0 && searchQuery === "") {
      setActiveClient(clients[0]);
    } else {
      setActiveClient(null);
    }
  }, [filteredClients]);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 2 }}>
        <Button onClick={() => setOpenAddClient(true)} fullWidth sx={{ mb: 2 }}>
          Add Client
        </Button>
        <TextField
          inputRef={searchInputRef}
          fullWidth
          size="small"
          placeholder="Search clients..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
        />
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
      >
        {filteredClients.length ? (
          filteredClients.map((client) => (
            <Paper
              sx={{
                mx: 2,
                mb: 2,
                p: 2,
                backgroundColor:
                  activeClient?.id === client.id
                    ? theme.palette.primary.main
                    : "white",
                color: activeClient?.id === client.id ? "white" : "inherit",
                cursor: "pointer",
                overflow: "hidden",
              }}
              key={client.id}
              onClick={() => {
                setActiveClient(client);
                if (isMobile) setDrawerOpen(false);
              }}
            >
              <Typography
                color={activeClient?.id === client.id ? "white" : "inherit"}
                sx={{
                  "& .email": {
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <b>{client.name}</b>
                <br />
                <span className="email">{client.email}</span>
              </Typography>
            </Paper>
          ))
        ) : searchQuery ? (
          <Paper sx={{ m: 2, p: 2 }}>
            <Typography sx={{ textAlign: "center", color: "text.secondary" }}>
              No clients found matching "{searchQuery}"
            </Typography>
          </Paper>
        ) : (
          <Paper sx={{ m: 2, p: 2 }}>
            <Box>
              <Typography sx={{ textAlign: "center", color: "red" }}>
                <b>You have no clients!</b>
              </Typography>
              <Typography sx={{ mt: 2, mb: 3, textAlign: "center" }}>
                Click above to invite a client to join you on Trainer App.
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

const Clients = () => {
  const { getClients } = useApi();
  const [clients, setClients] = useState([]);
  const [openAddClient, setOpenAddClient] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeClient, setActiveClient] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [productsDialogOpen, setProductsDialogOpen] = useState(false);
  const [clientHeaderActions, setClientHeaderActions] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false);
    } else {
      setDrawerOpen(true);
    }
  }, [isMobile]);

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const fetchClients = async () => {
    setLoading(true);
    const c = await getClients();
    const activeClients = c.filter((client) => !client.deleted);
    setClients(activeClients);
    if (activeClients.length > 0) {
      setActiveClient(activeClients[0]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchClients();
  }, []);

  if (loading) {
    return (
      <Container sx={{ mt: 10 }}>
        <Loading />
      </Container>
    );
  }

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 64px)" }}>
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            top: "64px",
            height: "calc(100% - 64px)",
          },
        }}
      >
        <ClientsList
          clients={clients}
          activeClient={activeClient}
          setActiveClient={setActiveClient}
          setOpenAddClient={setOpenAddClient}
          isMobile={isMobile}
          setDrawerOpen={setDrawerOpen}
          theme={theme}
        />
      </Drawer>

      <Box
        sx={{
          width: "100%",
          height: "100%",
          overflow: "auto",
          ml: drawerOpen ? "400px" : 0,
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton
                onClick={toggleDrawer}
                sx={{
                  display: { md: "block" },
                  backgroundColor: "background.paper",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                  p: 0,
                }}
              >
                <MenuIcon />
              </IconButton>
              {activeClient && (
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {activeClient.name}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {clientHeaderActions}
            </Box>
          </Box>
          {activeClient ? (
            <ClientWorkoutCalendar
              clientId={activeClient.id}
              onClientDeleted={() => {
                setActiveClient(null);
                fetchClients();
              }}
              setHeaderActions={setClientHeaderActions}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Select a client to view their workouts
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {openAddClient && (
        <AddClient
          onClose={() => setOpenAddClient(false)}
          onAddClient={() => {
            fetchClients();
          }}
        />
      )}
      <ManageProductsDialog
        open={productsDialogOpen}
        onClose={() => setProductsDialogOpen(false)}
        client={activeClient}
      />
    </Box>
  );
};

export default Clients;
